// Reusable Glassmorphism Card Component
// Enhanced glass effects with multiple variants

export function GlassCard({ 
  children, 
  className = '', 
  interactive = false,
  glow = false,
  variant = 'light', // 'light', 'dark', 'frosted', 'neon'
  onClick,
  ...props 
}) {
  const variants = {
    light: `
      bg-white/80 backdrop-blur-xl
      border border-white/60 
      shadow-lg shadow-gray-200/50
    `,
    dark: `
      bg-slate-900/80 backdrop-blur-xl
      border border-white/10 
      shadow-lg shadow-black/20
      text-white
    `,
    frosted: `
      bg-white/40 backdrop-blur-2xl
      border border-white/30 
      shadow-xl shadow-white/20
    `,
    neon: `
      bg-black/60 backdrop-blur-xl
      border border-emerald-500/30 
      shadow-lg shadow-emerald-500/20
      text-white
    `
  };
  
  const baseClasses = `
    relative overflow-hidden rounded-2xl 
    transition-all duration-300
    ${variants[variant] || variants.light}
  `;
  
  const interactiveClasses = interactive 
    ? 'hover:-translate-y-1 hover:shadow-2xl hover:border-opacity-80 cursor-pointer transform active:scale-[0.98]' 
    : '';
  
  const glowClasses = glow 
    ? 'shadow-[0_0_30px_rgba(16,185,129,0.3)] border-emerald-400/60' 
    : '';

  return (
    <div
      className={`${baseClasses} ${interactiveClasses} ${glowClasses} ${className}`}
      onClick={onClick}
      {...props}
    >
      {/* Subtle gradient overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/5 pointer-events-none" />
      <div className="relative">
        {children}
      </div>
    </div>
  );
}

// Glass Panel - for larger containers/sections
export function GlassPanel({
  children,
  className = '',
  variant = 'light',
  blur = 'xl', // 'sm', 'md', 'lg', 'xl', '2xl', '3xl'
  ...props
}) {
  const blurClasses = {
    sm: 'backdrop-blur-sm',
    md: 'backdrop-blur-md',
    lg: 'backdrop-blur-lg',
    xl: 'backdrop-blur-xl',
    '2xl': 'backdrop-blur-2xl',
    '3xl': 'backdrop-blur-[40px]'
  };

  const variants = {
    light: 'bg-white/70 border-white/50',
    dark: 'bg-slate-900/70 border-white/10 text-white',
    gradient: 'bg-gradient-to-br from-white/80 to-white/40 border-white/60'
  };

  return (
    <div
      className={`
        rounded-3xl border shadow-xl
        ${blurClasses[blur] || blurClasses.xl}
        ${variants[variant] || variants.light}
        ${className}
      `}
      {...props}
    >
      {children}
    </div>
  );
}

// Stat Card with animated number
export function StatCard({ 
  icon, 
  label, 
  value, 
  prefix = '$',
  trend,
  trendLabel,
  colorClass = 'text-emerald-600',
  variant = 'light'
}) {
  return (
    <GlassCard className="p-5" variant={variant}>
      <div className="flex items-start justify-between">
        <div className="text-3xl">{icon}</div>
        {trend !== undefined && (
          <span className={`text-xs font-bold px-2 py-1 rounded-full ${
            trend > 0 ? 'bg-green-100/80 text-green-600' : 'bg-red-100/80 text-red-600'
          }`}>
            {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className={`text-3xl font-black mt-3 ${colorClass}`}>
        {prefix}{typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div className="text-gray-500 text-sm mt-1">{label}</div>
      {trendLabel && (
        <div className="text-gray-400 text-xs mt-2">{trendLabel}</div>
      )}
    </GlassCard>
  );
}

// Progress Card with ring
export function ProgressCard({ title, current, total, icon, color = '#10B981', variant = 'light' }) {
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
  
  return (
    <GlassCard className="p-5" variant={variant}>
      <div className="flex items-center gap-4">
        <div className="relative w-16 h-16">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
            <circle
              cx="18"
              cy="18"
              r="15.5"
              fill="none"
              stroke="rgba(0,0,0,0.08)"
              strokeWidth="3"
            />
            <circle
              cx="18"
              cy="18"
              r="15.5"
              fill="none"
              stroke={color}
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={`${percentage}, 100`}
              style={{
                filter: `drop-shadow(0 0 6px ${color})`,
                transition: 'stroke-dasharray 0.5s ease-out'
              }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg">{icon}</span>
          </div>
        </div>
        <div className="flex-1">
          <div className="text-gray-900 font-bold">{title}</div>
          <div className="text-gray-500 text-sm">{current} / {total}</div>
          <div className="text-emerald-600 font-black text-lg">{percentage}%</div>
        </div>
      </div>
    </GlassCard>
  );
}

// Glass Button - for CTAs with glass effect
export function GlassButton({
  children,
  className = '',
  variant = 'primary', // 'primary', 'secondary', 'ghost'
  size = 'md',
  ...props
}) {
  const variants = {
    primary: 'bg-emerald-500/90 hover:bg-emerald-500 text-white border-emerald-400/50 shadow-emerald-500/25',
    secondary: 'bg-white/80 hover:bg-white text-gray-900 border-white/60 shadow-gray-200/50',
    ghost: 'bg-white/20 hover:bg-white/30 text-white border-white/30'
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2',
    lg: 'px-6 py-3 text-lg'
  };

  return (
    <button
      className={`
        rounded-xl font-semibold backdrop-blur-md border
        transition-all duration-200 shadow-lg
        hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0
        ${variants[variant] || variants.primary}
        ${sizes[size] || sizes.md}
        ${className}
      `}
      {...props}
    >
      {children}
    </button>
  );
}

export default GlassCard;
